import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Loader2, AlertCircle, CheckCircle, Copy, Download } from 'lucide-react';
import { listContentItems, generateReport, getReportEntities } from '../services/api';
import type { ContentItem, GeneratedReport } from '../types/v2';

export function ReportBuilderPage() {
  const navigate = useNavigate();

  const [syncedItems, setSyncedItems]       = useState<ContentItem[]>([]);
  const [selectedIds, setSelectedIds]       = useState<string[]>([]);
  const [focusQuery, setFocusQuery]         = useState('');
  const [suggestions, setSuggestions]       = useState<string[]>([]);
  const [reportStyle, setReportStyle]       = useState<'short' | 'analysis' | 'dossier'>('analysis');
  const [language, setLanguage]             = useState<'it' | 'en'>('it');
  const [generateImage, setGenerateImage]   = useState(true);
  const [loading, setLoading]               = useState(false);
  const [report, setReport]                 = useState<GeneratedReport | null>(null);
  const [error, setError]                   = useState<string | null>(null);
  const [copied, setCopied]                 = useState(false);

  useEffect(() => {
    listContentItems()
      .then((items) => {
        const synced = items.filter((i) => i.pipeline_status === 'GRAPH_SYNCED');
        setSyncedItems(synced);
        setSelectedIds(synced.map((i) => i.id));
      })
      .catch(() => {});
  }, []);

  const fetchSuggestions = useCallback(async (q: string) => {
    if (q.length < 2) { setSuggestions([]); return; }
    try {
      const entities = await getReportEntities(q);
      setSuggestions(entities.slice(0, 8));
    } catch {
      setSuggestions([]);
    }
  }, []);

  const toggleDoc = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((d) => d !== id) : [...prev, id]
    );
  };

  const STYLE_OPTIONS = [
    { value: 'short',    label: 'Notizia breve (~300p)' },
    { value: 'analysis', label: 'Analisi approfondita (~800p)' },
    { value: 'dossier',  label: 'Dossier investigativo (~1500p)' },
  ] as const;

  const handleGenerate = async () => {
    if (!focusQuery.trim()) { setError('Inserisci un argomento di focus'); return; }
    if (selectedIds.length === 0) { setError('Seleziona almeno un documento'); return; }
    setLoading(true);
    setError(null);
    setReport(null);
    try {
      const res = await generateReport({
        focusType:      'query',
        focus:          { queryText: focusQuery },
        reportType:     'cross_document',
        language,
        save:           true,
        generateImage,
        contentItemIds: selectedIds,
        options: {
          style: reportStyle,
        },
      });
      setReport(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore generazione articolo');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    if (!report) return;
    navigator.clipboard.writeText(report.body).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleDownload = () => {
    if (!report) return;
    const blob = new Blob([report.body], { type: 'text/plain' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `${report.title.slice(0, 60).replace(/[^\w\s-]/g, '')}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="flex h-full overflow-hidden">
      {/* Form */}
      <div className="w-96 shrink-0 overflow-y-auto p-6 border-r border-gray-800 space-y-5">
        <div className="flex items-center gap-3">
          <FileText className="w-5 h-5 text-blue-400" />
          <h2 className="text-lg font-semibold">Genera articolo</h2>
        </div>

        {/* Focus */}
        <div className="space-y-1.5">
          <label className="block text-xs text-gray-400">Argomento / focus</label>
          <input
            type="text"
            value={focusQuery}
            onChange={(e) => { setFocusQuery(e.target.value); fetchSuggestions(e.target.value); }}
            placeholder="es. immigrazione, Giorgia Meloni..."
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
          />
          {suggestions.length > 0 && (
            <div className="bg-gray-800 border border-gray-700 rounded-lg overflow-hidden">
              {suggestions.map((s) => (
                <button
                  key={s}
                  onClick={() => { setFocusQuery(s); setSuggestions([]); }}
                  className="w-full text-left px-3 py-1.5 text-xs text-gray-300 hover:bg-gray-700 transition-colors"
                >
                  {s}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Style */}
        <div className="space-y-1.5">
          <label className="block text-xs text-gray-400">Stile articolo</label>
          <select
            value={reportStyle}
            onChange={(e) => setReportStyle(e.target.value as typeof reportStyle)}
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500"
          >
            {STYLE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>

        {/* Language */}
        <div className="space-y-1.5">
          <label className="block text-xs text-gray-400">Lingua</label>
          <div className="flex gap-2">
            {(['it', 'en'] as const).map((lang) => (
              <button
                key={lang}
                onClick={() => setLanguage(lang)}
                className={`flex-1 py-2 text-sm rounded-lg border transition-colors ${
                  language === lang ? 'bg-blue-600 border-blue-500 text-white' : 'border-gray-700 text-gray-400 hover:text-white'
                }`}
              >
                {lang === 'it' ? 'Italiano' : 'English'}
              </button>
            ))}
          </div>
        </div>

        {/* Image toggle */}
        <label className="flex items-center gap-3 cursor-pointer">
          <div className={`w-10 h-5 rounded-full transition-colors ${generateImage ? 'bg-blue-600' : 'bg-gray-700'} relative`}>
            <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform ${generateImage ? 'translate-x-5' : 'translate-x-0.5'}`} />
          </div>
          <input type="checkbox" checked={generateImage} onChange={(e) => setGenerateImage(e.target.checked)} className="hidden" />
          <span className="text-xs text-gray-400">Genera immagine di copertina (DALL-E 3)</span>
        </label>

        {/* Documents */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <label className="block text-xs text-gray-400">Documenti ({selectedIds.length}/{syncedItems.length} selezionati)</label>
            <button
              onClick={() => setSelectedIds(
                selectedIds.length === syncedItems.length ? [] : syncedItems.map((i) => i.id)
              )}
              className="text-xs text-blue-400 hover:text-blue-300"
            >
              {selectedIds.length === syncedItems.length ? 'Deseleziona' : 'Seleziona tutti'}
            </button>
          </div>
          {syncedItems.length === 0 ? (
            <p className="text-xs text-gray-600">Nessun documento nel grafo. Vai alla Knowledge Base.</p>
          ) : (
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {syncedItems.map((item) => (
                <label key={item.id} className="flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer hover:bg-gray-800">
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(item.id)}
                    onChange={() => toggleDoc(item.id)}
                    className="accent-blue-500"
                  />
                  <span className="text-xs text-gray-300 truncate">{item.title || item.id.slice(0, 12)}</span>
                  <span className="text-xs text-gray-600 shrink-0">{item.entity_count}e</span>
                </label>
              ))}
            </div>
          )}
        </div>

        {error && (
          <div className="flex items-center gap-2 text-red-400 text-sm bg-red-900/20 border border-red-800 rounded-lg px-4 py-2">
            <AlertCircle className="w-4 h-4 shrink-0" /> {error}
          </div>
        )}

        <button
          onClick={handleGenerate}
          disabled={loading || !focusQuery.trim()}
          className="w-full bg-blue-600 hover:bg-blue-500 disabled:bg-gray-700 disabled:text-gray-500 text-white text-sm py-2.5 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
        >
          {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Generando...</> : 'Genera articolo'}
        </button>
      </div>

      {/* Preview */}
      <div className="flex-1 overflow-y-auto p-6">
        {loading && (
          <div className="flex flex-col items-center justify-center h-full text-gray-400 gap-3">
            <Loader2 className="w-10 h-10 animate-spin" />
            <p className="text-sm">Generazione articolo in corso...</p>
          </div>
        )}

        {!loading && !report && (
          <div className="flex flex-col items-center justify-center h-full text-gray-600 gap-3">
            <FileText className="w-16 h-16 text-gray-800" />
            <p className="text-sm">L&apos;articolo generato apparirà qui.</p>
          </div>
        )}

        {report && (
          <div className="max-w-2xl space-y-5">
            {/* Toolbar */}
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-semibold flex-1">{report.title}</h2>
              <button
                onClick={handleCopy}
                className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-white border border-gray-700 rounded-lg px-3 py-1.5 transition-colors"
              >
                {copied ? <><CheckCircle className="w-3.5 h-3.5 text-green-400" /> Copiato</> : <><Copy className="w-3.5 h-3.5" /> Copia</>}
              </button>
              <button
                onClick={handleDownload}
                className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-white border border-gray-700 rounded-lg px-3 py-1.5 transition-colors"
              >
                <Download className="w-3.5 h-3.5" /> TXT
              </button>
              {(report as GeneratedReport & { id?: number }).id && (
                <button
                  onClick={() => navigate(`/publish/${(report as GeneratedReport & { id?: number }).id}`)}
                  className="flex items-center gap-1.5 text-xs bg-blue-600 hover:bg-blue-500 text-white rounded-lg px-3 py-1.5 transition-colors"
                >
                  Pubblica →
                </button>
              )}
            </div>

            {/* Cover image */}
            {(report as GeneratedReport & { image_url?: string }).image_url && (
              <img
                src={(report as GeneratedReport & { image_url?: string }).image_url!}
                alt="Copertina articolo"
                className="w-full rounded-xl object-cover max-h-64"
              />
            )}

            {/* Body */}
            <div className="prose prose-invert prose-sm max-w-none bg-gray-900 border border-gray-800 rounded-xl p-6">
              <div className="whitespace-pre-wrap text-sm text-gray-200 leading-relaxed">
                {report.body}
              </div>
            </div>

            {/* Citations */}
            {report.citations && report.citations.length > 0 && (
              <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 space-y-2">
                <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Fonti ({report.citations.length})</h4>
                <ul className="space-y-1">
                  {report.citations.map((c, i) => (
                    <li key={i} className="text-xs text-gray-400">
                      {c.url ? (
                        <a href={c.url} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300">
                          {c.title || c.url}
                        </a>
                      ) : (
                        <span>{c.title || c.id}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
